import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Logo, PAGE_CONTAINER } from "../../components/site";

type AanvraagRij = {
  id: string;
  created_at: string;
  postcode: string;
  bron: string;
  product: string | null;
  ai_categorie: string | null;
  type_opdracht: string | null;
  omschrijving: string | null;
  partner_verzendstatus: string;
  partner_status: string;
};

function partnerStatusVan(aanvraag: AanvraagRij) {
  if (aanvraag.partner_verzendstatus === "mislukt") {
    return { label: "Doorsturen mislukt", className: "bg-destructive/10 text-destructive" };
  }
  if (aanvraag.partner_status === "geaccepteerd") {
    return { label: "Aangenomen door vakman", className: "bg-primary/10 text-primary" };
  }
  if (aanvraag.partner_status === "afgewezen") {
    return { label: "Afgewezen door partner", className: "bg-destructive/10 text-destructive" };
  }
  if (aanvraag.partner_status === "ontvangen" || aanvraag.partner_verzendstatus === "ontvangen") {
    return { label: "Ontvangen door partner", className: "bg-primary/10 text-primary" };
  }
  return { label: "Nog niet doorgestuurd", className: "bg-secondary text-muted-foreground" };
}

export const Route = createFileRoute("/_authenticated/verzonden-aanvragen")({
  head: () => ({
    meta: [
      { title: "Verzonden aanvragen | offerto" },
      { name: "description", content: "Privéoverzicht van verzonden offerteaanvragen voor beheerders." },
      { property: "og:title", content: "Verzonden aanvragen | offerto" },
      { property: "og:description", content: "Privéoverzicht van verzonden offerteaanvragen voor beheerders." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: VerzondenAanvragen,
});

function VerzondenAanvragen() {
  const { data: isAdmin, isPending: rolLaden, isError: rolFout } = useQuery({
    queryKey: ["is-admin"],
    queryFn: async () => {
      const { data: userData, error: gebruikerFout } = await supabase.auth.getUser();
      if (gebruikerFout || !userData.user) return false;
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: userData.user.id,
        _role: "admin",
      });
      if (error) throw error;
      return data === true;
    },
  });

  const { data: aanvragen, isPending: laden, isError } = useQuery({
    queryKey: ["verzonden-aanvragen"],
    enabled: isAdmin === true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("id, created_at, postcode, bron, product, ai_categorie, type_opdracht, omschrijving, partner_verzendstatus, partner_status")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className={`${PAGE_CONTAINER} flex items-center justify-between gap-4 py-4`}>
          <Logo />
          <Link to="/admin" className="text-sm font-semibold text-primary hover:underline">Terug naar beheer</Link>
        </div>
      </header>
      <main className={`${PAGE_CONTAINER} py-8`}>
        <h1 className="text-2xl font-bold">Verzonden aanvragen</h1>
        {rolLaden && <p className="mt-8 text-muted-foreground">Toegang controleren…</p>}
        {rolFout && <p className="mt-8 text-destructive">Toegang kon niet worden gecontroleerd. Probeer het opnieuw.</p>}
        {!rolLaden && !rolFout && !isAdmin && <p className="mt-8 text-muted-foreground">Geen toegang tot dit overzicht.</p>}
        {isAdmin && (
          <>
            {laden && <p className="mt-8 text-muted-foreground">Aanvragen laden…</p>}
            {isError && <p className="mt-8 text-destructive">Aanvragen konden niet worden geladen. Probeer het opnieuw.</p>}
            {aanvragen?.length === 0 && <p className="mt-8 text-muted-foreground">Er zijn nog geen aanvragen verzonden.</p>}
            {aanvragen && aanvragen.length > 0 && (
              <div className="mt-6 overflow-x-auto border-y border-border">
                <table className="w-full min-w-[750px] text-left text-sm">
                  <thead className="bg-secondary text-foreground">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">Datum</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Postcode</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Bron</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Product</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Categorie</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Werkzaamheden</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Status partner</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {aanvragen.map((aanvraag) => {
                      const status = partnerStatusVan(aanvraag);
                      return (
                        <tr key={aanvraag.id} className="align-top">
                          <td className="whitespace-nowrap px-4 py-4 text-muted-foreground">
                            <time dateTime={aanvraag.created_at}>
                              {new Date(aanvraag.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </time>
                          </td>
                          <td className="px-4 py-4 font-semibold">{aanvraag.postcode}</td>
                          <td className="px-4 py-4 break-words">{aanvraag.bron}</td>
                          <td className="max-w-sm px-4 py-4 break-words">{aanvraag.product || "—"}</td>
                          <td className="px-4 py-4">{aanvraag.ai_categorie || aanvraag.type_opdracht || "—"}</td>
                          <td className="max-w-sm whitespace-pre-wrap break-words px-4 py-4">{aanvraag.omschrijving || aanvraag.type_opdracht || "—"}</td>
                          <td className="whitespace-nowrap px-4 py-4">
                            <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}>{status.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}